/**
 * useMagicLink Hook - Magic link authentication logic
 *
 * Handles magic link verification without component involvement.
 */

import { useRef } from 'react';
import { randomString, sha256Base64Url, PKCE_SESSION_KEY_PREFIX } from '@/lib/pkce';

interface MagicLinkResponse {
  success: boolean;
  access_token?: string;
  refresh_token?: string;
  authenticated?: boolean;
  redirectUrl?: string;
  user?: {
    id: string;
    email: string;
    name: string;
  };
  apiKey?: string;
  isNewUser?: boolean;
  isExistingUser?: boolean;
  hasEntitlement?: boolean;
  message?: string;
  redirect?: string;
}

export const useMagicLink = (
  setTokens: (accessToken: string, refreshToken?: string) => void,
  setStatus: (status: 'idle' | 'processing' | 'success' | 'error') => void,
  setError: (error?: string) => void,
  setRedirect?: (redirect?: string) => void
) => {
  const processing = useRef<Set<string>>(new Set());

  const verify = (token: string) => {
    if (processing.current.has(token)) {
      return;
    }

    processing.current.add(token);
    setStatus('processing');

    const apiUrl = import.meta.env.VITE_API_URL || '';

    fetch(`${apiUrl}/v2/auth/magic-link/${token}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    })
      .then(async (response) => {
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
          console.error('[MagicLink] Verification failed:', errorData.message || response.status);
          setStatus('error');
          setError(errorData.message || `HTTP ${response.status}`);
          return;
        }

        const data: MagicLinkResponse = await response.json();

        if (!data.success) {
          setStatus('error');
          setError('Invalid response from server');
          return;
        }

        // Store API key if provided (both new and existing users)
        // Magic link proves email ownership, so it's safe to return the key
        if (data.apiKey) {
          localStorage.setItem('guardrail_api_key', data.apiKey);
        } else if (!data.hasEntitlement) {
          console.warn('[MagicLink] User lacks entitlement - no API key provided');
        }

        // Store redirect path if provided (e.g., /signup for users without entitlements)
        if (data.redirect && setRedirect) {
          setRedirect(data.redirect);
        }

        if (data.redirectUrl) {
          const state = randomString(16);
          const codeVerifier = randomString(64);
          const codeChallenge = await sha256Base64Url(codeVerifier);
          const authUrl = new URL(data.redirectUrl);

          authUrl.searchParams.set('state', state);
          authUrl.searchParams.set('code_challenge', codeChallenge);
          authUrl.searchParams.set('code_challenge_method', 'S256');
          sessionStorage.setItem(`${PKCE_SESSION_KEY_PREFIX}${state}`, codeVerifier);

          window.location.assign(authUrl.toString());
          return;
        }

        // Magic-link auth is JWT-only: success without access token is invalid.
        if (!data.access_token) {
          console.error('[MagicLink] Server returned success but no JWT was provisioned');
          setStatus('error');
          setError('Login succeeded but a session token could not be issued. Please try again.');
          return;
        }

        setTokens(data.access_token, data.refresh_token);

        setStatus('success');
      })
      .catch((error) => {
        console.error(
          '[MagicLink] Verification error:',
          error instanceof Error ? error.message : 'Unknown error'
        );
        setStatus('error');
        setError(error instanceof Error ? error.message : 'Unknown error');
      })
      .finally(() => {
        processing.current.delete(token);
      });
  };

  return { verify };
};
